import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TEST_IDS } from "@/constants/testIds";
import { formatApiError } from "@/lib/api";
import { motion } from "framer-motion";
import { UserPlus } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

export default function Register() {
  const { register } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError(t("auth.password_min")); return; }
    setLoading(true);
    try {
      await register(email, password, name, "", phone);
      navigate("/book");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[85vh] flex items-center justify-center px-4 py-12">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <Card className="border-border/50 bg-card/80 backdrop-blur-sm">
          <CardHeader className="text-center pb-2">
            <div className="w-12 h-12 rounded-xl bg-teal/10 flex items-center justify-center mx-auto mb-3">
              <UserPlus size={22} className="text-teal" />
            </div>
            <CardTitle className="font-heading text-2xl">{t("auth.join")}</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">{t("auth.create_subtitle")}</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && <div className="p-3 rounded-xl bg-destructive/10 text-destructive text-sm">{error}</div>}
              <div className="space-y-2">
                <Label htmlFor="name">{t("auth.name")}</Label>
                <Input id="name" data-testid={TEST_IDS.REGISTER_NAME} value={name} onChange={e => setName(e.target.value)} placeholder={t("auth.name_ph")} className="h-11 rounded-xl" required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">{t("auth.email")}</Label>
                <Input id="email" data-testid={TEST_IDS.REGISTER_EMAIL} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" className="h-11 rounded-xl" required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t("auth.password")}</Label>
                <Input id="password" data-testid={TEST_IDS.REGISTER_PASSWORD} type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder={t("auth.password_min_ph")} className="h-11 rounded-xl" required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">{t("auth.phone")}</Label>
                <Input id="phone" data-testid="register-phone" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+230 5xxx xxxx" className="h-11 rounded-xl" />
              </div>
              <Button type="submit" data-testid={TEST_IDS.REGISTER_SUBMIT} disabled={loading} className="w-full rounded-full bg-coral hover:bg-coral-600 text-white h-11 font-semibold">
                {loading ? t("auth.creating") : t("auth.create_account")}
              </Button>
              <p className="text-center text-sm text-muted-foreground">
                {t("auth.has_account")} <Link to="/login" className="text-coral hover:underline font-medium">{t("auth.signin_link")}</Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
